import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatDietTypeLabel,
  formatFoodItemDisplayName,
  loadGroupedOnlineMenuItems,
} from './onlineFoodItems';
import { AgePricingComboCell } from './AgePricingDisplay';
import './css/JreatsStatewideMenuBrowser.css';

/**
 * Read-only browser for the statewide Jr Eats catalog (managed in EAP).
 * Used from Daily Menu / Templates so restaurants don't need a Main Menu tab.
 */
export default function JreatsStatewideMenuBrowser({
  restaurantId,
  triggerLabel = 'View statewide dishes',
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stateLabel, setStateLabel] = useState('');
  const [grouped, setGrouped] = useState({});

  const flatItems = useMemo(() => {
    const rows = [];
    Object.entries(grouped || {}).forEach(([category, list]) => {
      (Array.isArray(list) ? list : []).forEach((item) => {
        rows.push({ category, item });
      });
    });
    return rows;
  }, [grouped]);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    setError('');
    try {
      const { items, meta } = await loadGroupedOnlineMenuItems(restaurantId, 'JREATS');
      setGrouped(items || {});
      setStateLabel(meta?.state || meta?.restaurantState || '');
    } catch (err) {
      setGrouped({});
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to load statewide Junior Eats menu',
      );
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!restaurantId) return null;

  return (
    <>
      <button
        type="button"
        className={
          compact
            ? 'jreats-statewide-browser__trigger jreats-statewide-browser__trigger--link'
            : 'jreats-statewide-browser__trigger'
        }
        onClick={() => setOpen(true)}
      >
        {triggerLabel}
      </button>

      {open ? (
        <div
          className="jreats-statewide-browser__overlay"
          role="presentation"
          onClick={() => setOpen(false)}
        >
          <div
            className="jreats-statewide-browser__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="jreats-statewide-browser-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="jreats-statewide-browser__header">
              <div>
                <h3 id="jreats-statewide-browser-title">Statewide Jr Eats dishes</h3>
                <p>
                  {stateLabel
                    ? `Catalog for ${stateLabel} — managed by Maya Eats Admin. Use templates to build your daily menu.`
                    : 'Catalog managed by Maya Eats Admin. Use templates to build your daily menu.'}
                </p>
              </div>
              <button
                type="button"
                className="jreats-statewide-browser__close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {loading ? (
              <p className="jreats-statewide-browser__muted">Loading…</p>
            ) : error ? (
              <p className="jreats-statewide-browser__error">{error}</p>
            ) : flatItems.length === 0 ? (
              <p className="jreats-statewide-browser__muted">
                No statewide dishes for this restaurant&apos;s state yet.
              </p>
            ) : (
              <div className="jreats-statewide-browser__table-wrap">
                <table className="jreats-statewide-browser__table">
                  <thead>
                    <tr>
                      <th>Dish</th>
                      <th>Category</th>
                      <th>Type</th>
                      <th>Price &amp; quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flatItems.map(({ category, item }) => {
                      const id =
                        item.Food_Item_Id ?? item.food_item_id ?? item.foodItemId ?? item.Food_Item;
                      return (
                        <tr key={`${category}-${id}`}>
                          <td>{formatFoodItemDisplayName(item.Food_Item ?? item.foodItem)}</td>
                          <td>{category}</td>
                          <td>{formatDietTypeLabel(item.itemFoodType ?? item.Food_Type)}</td>
                          <td>
                            <AgePricingComboCell
                              item={item}
                              fallbackPrice={item.Price ?? item.price}
                              fallbackQuantity={item.Quantity ?? item.quantity}
                              currency={item.Currency ?? item.currency}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
